import api from './api/apiClient';

// 세분화된 메뉴 권한 (조회/수정/삭제)
export interface MenuPermissionDetail {
  view: boolean;
  edit: boolean;
  delete: boolean;
}

// 메뉴 권한: null=전체허용(admin), string[]=하위호환, Record=세분화
export type MenuPermissions =
  | Record<string, MenuPermissionDetail>
  | string[]
  | null;

// 하위 호환: 기존 string[] → 세분화 객체로 변환
export const normalizePermissions = (
  perms: MenuPermissions
): Record<string, MenuPermissionDetail> | null => {
  if (perms === null || perms === undefined) return null;
  if (Array.isArray(perms)) {
    const result: Record<string, MenuPermissionDetail> = {};
    perms.forEach(key => {
      result[key] = { view: true, edit: false, delete: false };
    });
    return result;
  }
  return perms;
};

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  department?: string;
  position?: string;
  role: string;
  phone?: string;
  menu_permissions?: MenuPermissions;
  is_email_verified?: boolean;
  created_at: string;
  updated_at?: string;
}

export interface UpdateUserPayload {
  name?: string;
  email?: string;
  role?: string;
  department?: string;
  position?: string;
  phone?: string;
  menu_permissions?: MenuPermissions;
}

export interface WhitelistEntry {
  id: string;
  email: string;
  granted_by: string;
  created_at: string;
}

export interface OrgChartData {
  [department: string]: {
    [position: string]: AdminUser[];
  };
}

interface ApiListResponse<T> {
  success: boolean;
  data: T[];
}

interface ApiSingleResponse<T> {
  success: boolean;
  data: T;
}

const adminService = {
  getUsers: async (): Promise<AdminUser[]> => {
    const res = await api.get<ApiListResponse<AdminUser>>('/admin/users');
    return res.data;
  },

  getUser: async (id: string): Promise<AdminUser> => {
    const res = await api.get<ApiSingleResponse<AdminUser>>(`/admin/users/${id}`);
    return res.data;
  },

  updateUser: async (id: string, payload: UpdateUserPayload): Promise<AdminUser> => {
    const res = await api.put<ApiSingleResponse<AdminUser>>(`/admin/users/${id}`, payload);
    return res.data;
  },

  resetPassword: async (id: string, newPassword: string): Promise<void> => {
    await api.post('/admin/users/' + id + '/reset-password', { newPassword });
  },

  getOrgChart: async (): Promise<OrgChartData> => {
    const res = await api.get<ApiSingleResponse<OrgChartData>>('/admin/org-chart');
    return res.data;
  },

  // 프리셋 관리
  getPresets: async (): Promise<any[]> => {
    const res: any = await api.get('/admin/presets');
    return res.data;
  },

  createPreset: async (preset: { name: string; description?: string; permissions: Record<string, any> }): Promise<any> => {
    const res: any = await api.post('/admin/presets', preset);
    return res.data;
  },

  updatePreset: async (id: string, preset: { name?: string; description?: string; permissions?: Record<string, any> }): Promise<any> => {
    const res: any = await api.put(`/admin/presets/${id}`, preset);
    return res.data;
  },

  deletePreset: async (id: string): Promise<void> => {
    await api.delete(`/admin/presets/${id}`);
  },

  // AI 화이트리스트 관리
  getWhitelist: async (): Promise<WhitelistEntry[]> => {
    const res: any = await api.get('/admin/whitelist');
    return res.data;
  },

  addToWhitelist: async (email: string): Promise<WhitelistEntry> => {
    const res: any = await api.post('/admin/whitelist', { email });
    return res.data;
  },

  removeFromWhitelist: async (email: string): Promise<void> => {
    await api.delete(`/admin/whitelist/${encodeURIComponent(email)}`);
  },

  getOrgChartGraph: async (): Promise<{ nodes: any[]; edges: any[] }> => {
    const res: any = await api.get('/admin/org-chart/graph');
    return res.data || { nodes: [], edges: [] };
  },

  // ── 조직도 편집 (Phase 1) ───────────────────────────────────────────
  /** 팀 기본 정보 수정 — name/code/parent_team_id/sort_order/is_active */
  updateTeam: async (
    teamId: string,
    patch: { name?: string; code?: string; parent_team_id?: string | null; sort_order?: number; is_active?: boolean },
  ): Promise<any> => {
    const res: any = await api.patch(`/admin/teams/${teamId}`, patch);
    return res.data;
  },

  /** 팀원 추가 (겸직 가능) — role_in_team 기본 'member', position_level 기본 50 */
  addTeamMember: async (
    teamId: string,
    payload: { user_id: string; role_in_team?: 'leader' | 'deputy' | 'member' | 'observer'; position_level?: number },
  ): Promise<any> => {
    const res: any = await api.post(`/admin/teams/${teamId}/members`, payload);
    return res.data;
  },

  /** 팀원 역할/레벨 변경 (current row, effective_to IS NULL 기준) */
  updateTeamMember: async (
    teamId: string,
    userId: string,
    patch: { role_in_team?: 'leader' | 'deputy' | 'member' | 'observer'; position_level?: number },
  ): Promise<any> => {
    const res: any = await api.patch(`/admin/teams/${teamId}/members/${userId}`, patch);
    return res.data;
  },

  /** 팀에서 제외 — SCD-2 종료 (effective_to=NOW()) */
  removeTeamMember: async (teamId: string, userId: string): Promise<void> => {
    await api.delete(`/admin/teams/${teamId}/members/${userId}`);
  },
};

export default adminService;
